/*
  Masonry layout and Load more for the gallery grid
  (sections/gallery-grid.liquid). Loaded by that section only.

  The section prints every tile in one flat list, newest first, each with its
  image's shape in data-ratio. This script deals them into columns, always
  into the shortest one so far, which puts the newest tiles across the top
  row and keeps the column bottoms even. The column count follows the screen
  width: 2 on phones, 3 on tablets, 4 on desktop (with the design's 350 / 250
  / 400 / 250 widths). Heights are worked out from the data-ratio values, not
  measured, so nothing moves when the images arrive.

  The tiles beyond the first screenful are split into Load more groups. A
  tile's height is estimated from its column's width and its image's shape,
  and a tile whose middle falls within the first FOLD_HEIGHT is shown from the
  start; the next FOLD_HEIGHT's worth is one group, and so on, so every group
  is a balanced set. The groups are worked out again whenever the column count
  changes, and how many have been opened is kept. Hidden tiles get
  gallery-grid__tile--later (display: none), so their lazy images don't
  download until shown. Each click loads the next group's images while still
  hidden and then shows the whole group at once. Focus then moves to the first
  new tile so keyboard users carry on from there. The button goes away once
  nothing is hidden.
*/
(function () {
  // The section loads this file once per instance; set up only once.
  if (window.galleryGridLoaded) return;
  window.galleryGridLoaded = true;

  const LATER_CLASS = 'gallery-grid__tile--later';
  // Heights are in px at a 1280px wide page, the same units as the column
  // widths below, whatever the real screen is.
  const FOLD_HEIGHT = 1050;
  const TILE_GAP = 10;
  const DESKTOP_WEIGHTS = [350, 250, 400, 250];
  const TOTAL_WEIGHT = 1250;
  const DEFAULT_RATIO = 0.8;
  // A slow image shouldn't hold the row back for long.
  const MAX_IMAGE_WAIT = 3000;
  // Keep the button's spinner up long enough to be seen, even when the
  // images are already cached.
  const MIN_LOADING_TIME = 400;

  const desktopQuery = window.matchMedia('(min-width: 992px)');
  const tabletQuery = window.matchMedia('(min-width: 750px)');

  function columnCount() {
    if (desktopQuery.matches) return 4;
    if (tabletQuery.matches) return 3;
    return 2;
  }

  function columnWeights(count) {
    return count === 4 ? DESKTOP_WEIGHTS : Array(count).fill(TOTAL_WEIGHT / count);
  }

  // Hidden lazy images never load, so switch them to eager and wait until
  // they're ready to paint. A failed image counts as ready.
  function loadImages(tileNodes) {
    const images = tileNodes.flatMap((tileNode) => Array.from(tileNode.querySelectorAll('img')));
    const ready = images.map((imageNode) => {
      imageNode.loading = 'eager';
      return imageNode.decode().catch(() => {});
    });
    const timeout = new Promise((resolve) => setTimeout(resolve, MAX_IMAGE_WAIT));
    return Promise.race([Promise.all(ready), timeout]);
  }

  function initGallery(sectionNode) {
    const tilesNode = sectionNode.querySelector('.gallery-grid__tiles');
    const moreNode = sectionNode.querySelector('.gallery-grid__more');
    const buttonNode = sectionNode.querySelector('.gallery-grid__more-button');
    if (!tilesNode || !moreNode || !buttonNode || tilesNode.dataset.masonryInitialized === 'true') return;
    tilesNode.dataset.masonryInitialized = 'true';

    // The flat list, newest first. Kept in this order for good: the columns
    // are rebuilt from it whenever the column count changes.
    const tiles = Array.from(tilesNode.querySelectorAll('.hp-cat-bond__tile'));
    if (tiles.length === 0) return;

    // Which Load more group each tile is in, as a rank: 0 is shown from the
    // start, then 1, 2, 3 for each click.
    let ranks = [];
    let opened = 0;
    let currentCount = 0;

    const updateButton = () => {
      moreNode.hidden = !tilesNode.querySelector(`.${LATER_CLASS}`);
    };

    const applyVisibility = () => {
      tiles.forEach((tileNode, index) => {
        tileNode.classList.toggle(LATER_CLASS, ranks[index] > opened);
      });
      updateButton();
    };

    const layout = () => {
      const count = columnCount();
      if (count === currentCount) return;
      currentCount = count;

      const weights = columnWeights(count);
      const tops = Array(count).fill(0);
      const columnNodes = weights.map(() => {
        const columnNode = document.createElement('div');
        columnNode.className = 'gallery-grid__col';
        return columnNode;
      });

      // Deal each tile into the shortest column (the leftmost on a tie).
      const groups = tiles.map((tileNode) => {
        const ratio = parseFloat(tileNode.dataset.ratio) || DEFAULT_RATIO;
        let target = 0;
        for (let index = 1; index < count; index++) {
          if (tops[index] < tops[target]) target = index;
        }
        const height = weights[target] / ratio;
        const middle = tops[target] + height / 2;
        tops[target] += height + TILE_GAP;
        columnNodes[target].appendChild(tileNode);
        return Math.floor(middle / FOLD_HEIGHT);
      });

      // Group numbers can skip (a tall tile can span a whole fold), so they
      // become ranks: one per click, none skipped.
      const later = [...new Set(groups.filter((group) => group > 0))].sort((a, b) => a - b);
      ranks = groups.map((group) => (group > 0 ? later.indexOf(group) + 1 : 0));

      tilesNode.replaceChildren(...columnNodes);
      tilesNode.dataset.columns = String(count);
      applyVisibility();
    };

    buttonNode.addEventListener('click', async () => {
      if (buttonNode.getAttribute('aria-busy') === 'true') return;

      const next = opened + 1;
      const groupTiles = tiles.filter((tileNode, index) => ranks[index] === next);
      if (groupTiles.length === 0) return;

      buttonNode.setAttribute('aria-busy', 'true');
      await Promise.all([
        loadImages(groupTiles),
        new Promise((resolve) => setTimeout(resolve, MIN_LOADING_TIME)),
      ]);
      buttonNode.removeAttribute('aria-busy');

      opened = next;
      applyVisibility();
      // preventScroll: the first new tile may be off screen, and jumping the
      // page there on every click is disorienting.
      groupTiles[0].querySelector('.hp-cat-bond__open')?.focus({ preventScroll: true });
    });

    // Re-deal the tiles when the screen crosses a column breakpoint. The
    // theme editor swaps in fresh markup instead, so these only outlive the
    // old node harmlessly.
    desktopQuery.addEventListener('change', layout);
    tabletQuery.addEventListener('change', layout);

    layout();
  }

  function initAll(root) {
    root.querySelectorAll('.gallery-grid').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  // The theme editor swaps in fresh markup when the section's settings change.
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
